import { useEffect, useState } from "react";
import { Bot, Mail, Phone, Trash2, MessageSquare, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Modal from "@/components/admin/Modal";
import ExportCsvButton from "@/components/admin/ExportCsvButton";
import { PageHeader } from "./AdminFaq";

const REPLY_TEMPLATES = [
    {
        id: "orcamento",
        label: "Enviar orçamento",
        subject: "O seu orçamento NOVIFOOD",
        message: "Olá!\n\nObrigado pelo interesse nos carrinhos NOVIFOOD. Com base na conversa com o nosso assistente, preparámos as opções ideais para o seu negócio.\n\nPodemos enviar o orçamento detalhado ainda hoje — basta confirmar o modelo (Alfa ou Ômega) e os equipamentos que pretende.\n\nTambém pode configurar tudo ao seu gosto no nosso simulador: https://novifood.pt/simulador\n\nQualquer dúvida, estamos por aqui.",
    },
    {
        id: "chamada",
        label: "Agendar chamada",
        subject: "Vamos falar sobre o seu carrinho NOVIFOOD?",
        message: "Olá!\n\nVimos que esteve a explorar os nossos carrinhos com o assistente do site. Gostávamos de perceber melhor o seu projecto e mostrar-lhe o que é possível.\n\nTem disponibilidade para uma chamada rápida de 15 minutos esta semana? Diga-nos o dia e a hora que preferir.\n\nAté já!",
    },
    {
        id: "followup",
        label: "Follow-up",
        subject: "Ainda a pensar no seu food cart?",
        message: "Olá!\n\nHá uns dias mostrou interesse nos carrinhos NOVIFOOD e não queríamos deixar a conversa esfriar.\n\nSe ainda estiver a avaliar, teremos todo o gosto em esclarecer dúvidas sobre modelos, prazos ou financiamento — sem qualquer compromisso.\n\nBasta responder a este email.",
    },
];

const STATUS_META = {
    new: { label: "Novo", cls: "bg-amber-50 text-amber-700" },
    contacted: { label: "Contactado", cls: "bg-sky-50 text-sky-700" },
    converted: { label: "Convertido", cls: "bg-emerald-50 text-emerald-700" },
    archived: { label: "Arquivado", cls: "bg-zinc-100 text-zinc-600" },
};

export default function AdminChatLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [convo, setConvo] = useState(null);
    const [messages, setMessages] = useState([]);
    const [reply, setReply] = useState(null);
    const [sending, setSending] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/admin/chat-leads"); setLeads(data || []); }
        catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function openConvo(lead) {
        setConvo(lead);
        setMessages([]);
        setReply(null);
        try { const { data } = await api.get(`/admin/chat-leads/${lead.session_id}/messages`); setMessages(data || []); }
        catch { toast.error("Erro ao carregar conversa"); }
    }

    function applyTemplate(tpl) {
        setReply({ subject: tpl.subject, message: tpl.message, templateId: tpl.id });
    }

    async function sendReply() {
        if (!reply?.subject?.trim() || !reply?.message?.trim()) {
            toast.error("Preencha assunto e mensagem");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/admin/chat-leads/${convo.id}/reply`, { subject: reply.subject, message: reply.message });
            if (data.skipped) {
                toast.warning("SMTP não configurado — configure o email nas Definições para enviar diretamente.", { duration: 8000 });
            } else if (data.ok) {
                toast.success(`Email enviado para ${convo.email}`);
                setReply(null);
                setConvo((c) => ({ ...c, status: "contacted" }));
                load();
            } else {
                toast.error(data.error || "Falha no envio");
            }
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao enviar");
        } finally {
            setSending(false);
        }
    }

    async function setStatus(lead, status) {
        try { await api.patch(`/admin/chat-leads/${lead.id}`, { status }); load(); }
        catch { toast.error("Erro ao atualizar"); }
    }

    async function onDelete(lead) {
        if (!window.confirm("Apagar este lead?")) return;
        try { await api.delete(`/admin/chat-leads/${lead.id}`); toast.success("Apagado"); load(); }
        catch { toast.error("Erro ao apagar"); }
    }

    return (
        <div data-testid="admin-chat-leads-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <PageHeader title="Leads do chat" subtitle="Contactos deixados por visitantes no assistente IA" count={leads.length} />
                <ExportCsvButton endpoint="/admin/export/chat-leads" filename="novifood-leads-chat.csv" testId="export-chat-leads-csv" />
            </div>

            <div className="mt-8 rounded-2xl border border-border bg-white overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground">A carregar...</div>
                ) : leads.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground">
                        <Bot className="h-8 w-8 mx-auto mb-3 opacity-40" />
                        Ainda sem leads. Quando um visitante deixar email ou telemóvel no assistente, aparece aqui.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-muted/60 text-left">
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide">Contacto</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide">Última mensagem</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-24">Msgs</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-32">Estado</th>
                                <th className="px-5 py-3 font-bold text-xs uppercase tracking-wide w-44 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => {
                                const meta = STATUS_META[lead.status] || STATUS_META.new;
                                return (
                                    <tr key={lead.id} data-testid={`chat-lead-row-${lead.id}`} className="border-t border-border hover:bg-muted/30">
                                        <td className="px-5 py-3 align-top">
                                            {lead.email && <div className="inline-flex items-center gap-1.5 font-semibold text-primary"><Mail className="h-3.5 w-3.5 text-secondary" /> {lead.email}</div>}
                                            {lead.phone && <div className="inline-flex items-center gap-1.5 text-primary mt-0.5"><Phone className="h-3.5 w-3.5 text-secondary" /> {lead.phone}</div>}
                                            <div className="text-[11px] text-muted-foreground mt-1">{(lead.updated_at || "").slice(0, 16).replace("T", " ")}</div>
                                        </td>
                                        <td className="px-5 py-3 align-top text-muted-foreground">
                                            <span className="line-clamp-2">{lead.last_message}</span>
                                        </td>
                                        <td className="px-5 py-3 align-top text-muted-foreground">{lead.message_count}</td>
                                        <td className="px-5 py-3 align-top">
                                            <span className={`inline-flex rounded-md px-2 py-1 text-xs font-bold ${meta.cls}`}>{meta.label}</span>
                                            {lead.followup_sent_at && (
                                                <div className="mt-1 inline-flex rounded-md bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700" data-testid={`lead-followup-badge-${lead.id}`} title={`Follow-up automático enviado a ${(lead.followup_sent_at || "").slice(0, 16).replace("T", " ")}`}>
                                                    Follow-up ✓
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 align-top text-right">
                                            <div className="inline-flex gap-1.5">
                                                <button onClick={() => openConvo(lead)} data-testid={`chat-lead-view-${lead.id}`} title="Ver conversa"
                                                    className="rounded-md p-2 text-primary hover:bg-muted"><MessageSquare className="h-4 w-4" /></button>
                                                {lead.status === "new" && (
                                                    <button onClick={() => setStatus(lead, "contacted")} data-testid={`chat-lead-contacted-${lead.id}`} title="Marcar contactado"
                                                        className="rounded-md p-2 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /></button>
                                                )}
                                                <button onClick={() => onDelete(lead)} data-testid={`chat-lead-delete-${lead.id}`} title="Apagar"
                                                    className="rounded-md p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal open={!!convo} onClose={() => setConvo(null)} title={`Conversa — ${convo?.email || convo?.phone || ""}`} size="lg">
                <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1" data-testid="chat-lead-conversation">
                    {messages.length === 0 ? (
                        <p className="text-sm text-muted-foreground">A carregar conversa...</p>
                    ) : messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-white" : "bg-muted text-primary"}`}>
                                {m.content}
                                <div className={`mt-1 text-[10px] ${m.role === "user" ? "text-white/60" : "text-muted-foreground"}`}>
                                    {(m.created_at || "").slice(11, 16)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-4 pt-3 border-t border-border">
                    {convo?.email && (
                        <div className="mb-4" data-testid="lead-reply-section">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Resposta rápida</p>
                                <div className="flex gap-1.5 flex-wrap">
                                    {REPLY_TEMPLATES.map((tpl) => (
                                        <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`reply-template-${tpl.id}`}
                                            className={`text-xs rounded-full px-3 py-1.5 font-bold border transition ${reply?.templateId === tpl.id ? "bg-primary text-white border-primary" : "border-border text-primary hover:bg-muted"}`}>
                                            {tpl.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {reply && (
                                <div className="mt-3 space-y-2">
                                    <input
                                        value={reply.subject}
                                        onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))}
                                        data-testid="reply-subject"
                                        className="w-full rounded-md border border-border px-3 py-2 text-sm font-semibold text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                        placeholder="Assunto"
                                    />
                                    <textarea
                                        rows={6}
                                        value={reply.message}
                                        onChange={(e) => setReply((r) => ({ ...r, message: e.target.value }))}
                                        data-testid="reply-message"
                                        className="w-full rounded-md border border-border px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] text-muted-foreground">Para: <strong>{convo.email}</strong></span>
                                        <div className="flex gap-2">
                                            <button onClick={() => setReply(null)} className="text-xs rounded-md px-3 py-2 font-bold text-muted-foreground hover:bg-muted">Cancelar</button>
                                            <button onClick={sendReply} disabled={sending} data-testid="reply-send"
                                                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-bold text-white hover:-translate-y-0.5 transition disabled:opacity-50">
                                                <Send className="h-3.5 w-3.5" /> {sending ? "A enviar..." : "Enviar email"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="flex justify-between items-center flex-wrap gap-2">
                    <div className="flex gap-2">
                        {Object.entries(STATUS_META).map(([s, meta]) => (
                            <button key={s} onClick={() => { setStatus(convo, s); setConvo((c) => ({ ...c, status: s })); }}
                                className={`text-xs rounded-md px-2.5 py-1.5 font-bold border transition ${convo?.status === s ? meta.cls + " border-transparent" : "border-border text-muted-foreground hover:bg-muted"}`}>
                                {meta.label}
                            </button>
                        ))}
                    </div>
                    {convo?.email && (
                        <a href={`mailto:${convo.email}`} className="text-xs font-bold text-primary underline">Abrir no meu email →</a>
                    )}
                    </div>
                </div>
            </Modal>
        </div>
    );
}
